(() => {
  const sections = document.querySelectorAll('[data-keeto-homepage]');

  const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
  const ease = (value) => value * value * (3 - 2 * value);

  const detectTone = (img, options = {}) => {
    try {
      const region = Object.assign({ x: 0, y: 0, w: 1, h: 1, threshold: 166 }, options);
      if (!img || !img.naturalWidth || !img.naturalHeight) return 'dark';
      const canvas = document.createElement('canvas');
      canvas.width = 32;
      canvas.height = 32;
      const context = canvas.getContext('2d', { willReadFrequently: true });
      context.drawImage(
        img,
        Math.floor(img.naturalWidth * region.x),
        Math.floor(img.naturalHeight * region.y),
        Math.max(1, Math.floor(img.naturalWidth * region.w)),
        Math.max(1, Math.floor(img.naturalHeight * region.h)),
        0,
        0,
        canvas.width,
        canvas.height
      );
      const pixels = context.getImageData(0, 0, canvas.width, canvas.height).data;
      let total = 0;
      let weight = 0;
      for (let index = 0; index < pixels.length; index += 4) {
        const alpha = pixels[index + 3] / 255;
        if (alpha < 0.35) continue;
        total += (0.299 * pixels[index] + 0.587 * pixels[index + 1] + 0.114 * pixels[index + 2]) * alpha;
        weight += alpha;
      }
      return weight && total / weight > region.threshold ? 'light' : 'dark';
    } catch (error) {
      return 'dark';
    }
  };

  const initialize = (root) => {
    if (root.dataset.keetoInitialized === 'true') return;
    root.dataset.keetoInitialized = 'true';

    const header = root.querySelector('[data-keeto-header]');
    const slider = root.querySelector('[data-slider]');
    const slides = slider ? [...slider.querySelectorAll('[data-slide]')] : [];
    const dots = slider ? [...slider.querySelectorAll('[data-dot]')] : [];
    let activeSlide = 0;
    let sliderTimer;
    const interval = Number(slider?.dataset.autoplayInterval || 6500);
    const autoplay = slider?.dataset.autoplay === 'true';

    const setHeroTone = (tone) => {
      const mode = tone === 'light' ? 'light' : 'dark';
      slider?.classList.remove('tone-light', 'tone-dark');
      slider?.classList.add(`tone-${mode}`);
      header?.classList.toggle('hero-light-mode', mode === 'light');
      header?.classList.toggle('hero-dark-mode', mode !== 'light');
    };

    const applyHeroTone = () => {
      const slide = slides[activeSlide];
      setHeroTone(slide?.dataset.resolvedTone || slide?.dataset.tone || 'dark');
    };

    slides.forEach((slide, index) => {
      const configuredTone = slide.dataset.tone || 'auto';
      const image = slide.querySelector('img');
      const resolve = () => {
        slide.dataset.resolvedTone = configuredTone === 'auto'
          ? detectTone(image, { x: 0.04, y: 0.06, w: 0.56, h: 0.72, threshold: 168 })
          : configuredTone;
        if (index === activeSlide) applyHeroTone();
      };
      if (image?.complete) resolve();
      else image?.addEventListener('load', resolve, { once: true });
    });

    const showSlide = (index) => {
      if (!slides.length) return;
      activeSlide = (index + slides.length) % slides.length;
      slides.forEach((slide, slideIndex) => slide.classList.toggle('active', slideIndex === activeSlide));
      dots.forEach((dot, dotIndex) => {
        dot.classList.toggle('active', dotIndex === activeSlide);
        dot.setAttribute('aria-current', dotIndex === activeSlide ? 'true' : 'false');
      });
      applyHeroTone();
    };

    const stopSlider = () => window.clearInterval(sliderTimer);
    const startSlider = () => {
      stopSlider();
      if (autoplay && slides.length > 1) sliderTimer = window.setInterval(() => showSlide(activeSlide + 1), interval);
    };

    slider?.querySelector('[data-prev]')?.addEventListener('click', () => { showSlide(activeSlide - 1); startSlider(); });
    slider?.querySelector('[data-next]')?.addEventListener('click', () => { showSlide(activeSlide + 1); startSlider(); });
    dots.forEach((dot, index) => dot.addEventListener('click', () => { showSlide(index); startSlider(); }));
    slider?.addEventListener('mouseenter', stopSlider);
    slider?.addEventListener('mouseleave', startSlider);
    showSlide(0);
    startSlider();

    const languageSwitcher = root.querySelector('[data-language-switcher]');
    const languageTrigger = languageSwitcher?.querySelector('[data-language-trigger]');
    const languageForm = languageSwitcher?.querySelector('form');
    const languageInput = languageSwitcher?.querySelector('input[name="language_code"]');
    languageTrigger?.addEventListener('click', () => {
      const isOpen = languageSwitcher.classList.toggle('open');
      languageTrigger.setAttribute('aria-expanded', String(isOpen));
    });
    languageSwitcher?.querySelectorAll('[data-language-code]').forEach((option) => {
      option.addEventListener('click', () => {
        if (!languageInput || !languageForm) return;
        languageInput.value = option.dataset.languageCode;
        languageForm.submit();
      });
    });
    document.addEventListener('click', (event) => {
      if (languageSwitcher && !languageSwitcher.contains(event.target)) {
        languageSwitcher.classList.remove('open');
        languageTrigger?.setAttribute('aria-expanded', 'false');
      }
    });

    const reviewSection = root.querySelector('.review-section');
    const reviewSticky = root.querySelector('.review-sticky');
    const reviewStage = root.querySelector('[data-review-stage]');
    const reviewPoster = root.querySelector('.review-poster');
    const reviewTitle = root.querySelector('.review-title-overlay');
    const reviewPosterImage = reviewPoster?.querySelector('img');
    const reviewTrack = root.querySelector('[data-review-track]');
    const reviewDots = [...root.querySelectorAll('[data-review-dot]')];
    let activeReview = 0;

    const reviewTone = reviewPoster?.dataset.tone || 'auto';
    const applyReviewTone = () => {
      if (!reviewPoster || !reviewTitle) return;
      const tone = reviewTone === 'auto' ? detectTone(reviewPosterImage, { threshold: 158 }) : reviewTone;
      reviewPoster.classList.toggle('tone-light', tone === 'light');
      reviewPoster.classList.toggle('tone-dark', tone !== 'light');
      reviewTitle.classList.toggle('is-light', tone === 'light');
      reviewTitle.classList.toggle('is-dark', tone !== 'light');
    };
    if (reviewPosterImage?.complete) applyReviewTone();
    else reviewPosterImage?.addEventListener('load', applyReviewTone, { once: true });

    const showReview = (index) => {
      if (!reviewDots.length || !reviewTrack) return;
      activeReview = (index + reviewDots.length) % reviewDots.length;
      reviewTrack.style.transform = `translateX(-${activeReview * 100}%)`;
      reviewDots.forEach((dot, dotIndex) => {
        dot.classList.toggle('active', dotIndex === activeReview);
        dot.setAttribute('aria-current', dotIndex === activeReview ? 'true' : 'false');
      });
    };
    root.querySelector('[data-review-prev]')?.addEventListener('click', () => showReview(activeReview - 1));
    root.querySelector('[data-review-next]')?.addEventListener('click', () => showReview(activeReview + 1));
    reviewDots.forEach((dot, index) => dot.addEventListener('click', () => showReview(index)));
    showReview(0);

    let touchStartX = 0;
    reviewTrack?.parentElement?.addEventListener('touchstart', (event) => { touchStartX = event.touches[0].clientX; }, { passive: true });
    reviewTrack?.parentElement?.addEventListener('touchend', (event) => {
      const delta = event.changedTouches[0].clientX - touchStartX;
      if (Math.abs(delta) > 50) showReview(activeReview + (delta < 0 ? 1 : -1));
    });

    let ticking = false;
    const updateReviewProgress = () => {
      if (!reviewSection || !reviewSticky || !reviewStage || !reviewPoster) return;
      const rect = reviewSection.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const stickyHeight = reviewSticky.offsetHeight;
      const travel = Math.max(1, reviewSection.offsetHeight - stickyHeight);
      const trigger = viewportHeight * 0.72;
      const seen = clamp(trigger - rect.top, 0, travel + trigger);
      const raw = clamp(seen / (travel + trigger), 0, 1);
      const expand = ease(clamp(raw / 0.58, 0, 1));
      const initialWidth = Math.min(540, window.innerWidth * 0.78);
      const initialHeight = Math.min(300, window.innerHeight * 0.38);
      const edgeX = window.innerWidth >= 900 ? 72 : 32;
      const edgeY = window.innerWidth >= 900 ? 54 : 28;
      const targetWidth = Math.max(initialWidth, window.innerWidth - edgeX);
      const targetHeight = Math.max(initialHeight, stickyHeight - edgeY);
      reviewStage.style.setProperty('--poster-w', `${initialWidth + (targetWidth - initialWidth) * expand}px`);
      reviewStage.style.setProperty('--poster-h', `${initialHeight + (targetHeight - initialHeight) * expand}px`);
      reviewStage.style.setProperty('--poster-pad', `${38 + (edgeY / 2 - 38) * expand}px`);
      reviewStage.style.setProperty('--img-scale', (1 + 0.03 * expand).toFixed(3));
      reviewPoster.style.borderRadius = `${24 - 10 * expand}px`;
      const titleProgress = ease(clamp((raw - 0.16) / 0.26, 0, 1));
      reviewStage.style.setProperty('--title-o', titleProgress.toFixed(3));
      reviewStage.style.setProperty('--title-y', `${22 - 22 * titleProgress}px`);
      const curtainProgress = ease(clamp((raw - 0.62) / 0.20, 0, 1));
      reviewStage.style.setProperty('--curtain-y', `${(1 - curtainProgress) * 100}%`);
      const coverProgress = ease(clamp((raw - 0.66) / 0.12, 0, 1));
      reviewStage.style.setProperty('--cover-o', coverProgress.toFixed(3));
      ticking = false;
    };
    const requestReviewUpdate = () => {
      if (!ticking) {
        window.requestAnimationFrame(updateReviewProgress);
        ticking = true;
      }
    };
    const pageWrapper = document.querySelector('.page-wrapper');
    const scrollSources = [window];
    if (pageWrapper) scrollSources.push(pageWrapper);
    updateReviewProgress();
    scrollSources.forEach((source) => source.addEventListener('scroll', requestReviewUpdate, { passive: true }));
    window.addEventListener('resize', requestReviewUpdate);
    if ('ResizeObserver' in window) {
      const reviewResizeObserver = new ResizeObserver(requestReviewUpdate);
      if (reviewSection) reviewResizeObserver.observe(reviewSection);
      if (reviewSticky) reviewResizeObserver.observe(reviewSticky);
    }

    root.querySelectorAll('.newsletter-form').forEach((form) => {
      form.addEventListener('submit', () => {
        if (!form.checkValidity()) return;
        const button = form.querySelector('button[type="submit"]');
        if (!button) return;
        button.disabled = true;
        button.textContent = button.dataset.loadingLabel || button.textContent;
        form.classList.add('is-loading');
      });
    });

    document.addEventListener('shopify:block:select', (event) => {
      const block = root.querySelector(`[data-block-id="${event.detail.blockId}"]`);
      if (!block) return;
      const slideIndex = slides.indexOf(block.closest('[data-slide]'));
      if (slideIndex >= 0) {
        stopSlider();
        showSlide(slideIndex);
      }
      const reviewSlide = block.closest('.review-slide');
      if (reviewSlide && reviewTrack) {
        const index = [...reviewTrack.children].indexOf(reviewSlide);
        if (index >= 0) showReview(index);
      }
    });
  };

  sections.forEach(initialize);
  document.addEventListener('shopify:section:load', (event) => {
    const root = event.target.querySelector?.('[data-keeto-homepage]') || (event.target.matches?.('[data-keeto-homepage]') ? event.target : null);
    if (root) initialize(root);
  });
})();
